// Reads the visitor's CookieScript choice out of `document.cookie`.
//
// CookieScript writes one cookie, `CookieScriptConsent`, as URL-encoded JSON. Captured from
// www.24rent.fi on 6 Oct 2026 in fresh profiles, accept then reject:
//
//   {"googleconsentmap":{"ad_storage":"targeting","analytics_storage":"performance",
//                        "ad_personalization":"targeting","ad_user_data":"targeting",
//                        "functionality_storage":"functionality",
//                        "personalization_storage":"functionality","security_storage":"functionality"},
//    "action":"accept","consenttime":1725951338,
//    "categories":"[\"targeting\",\"functionality\",\"performance\",\"unclassified\"]"}
//
//   {...same map..., "action":"reject","consenttime":1725951338,"categories":"[]"}
//
// Two things about that shape matter here. `categories` is itself a JSON string, not an array.
// And `googleconsentmap` maps Google's consent types to CookieScript's category names, so callers
// ask in Google's vocabulary (`analytics_storage`, `ad_storage`) and the mapping comes from the
// cookie rather than from a constant in this file; a fallback map is kept only for a cookie that
// omits it.
//
// This reader fails closed. Anything it cannot positively read as an accept that includes the
// category is `'denied'` (an explicit reject, or an accept without it) or `'unknown'` (no cookie,
// or a shape it does not recognise). It never throws: a bad cookie must not break a login.

export type ConsentState = 'granted' | 'denied' | 'unknown';

export type GoogleConsentType =
    | 'ad_storage'
    | 'analytics_storage'
    | 'ad_personalization'
    | 'ad_user_data'
    | 'functionality_storage'
    | 'personalization_storage'
    | 'security_storage';

export const COOKIE_SCRIPT_COOKIE = 'CookieScriptConsent';

// CookieScript's own category names, used only when the cookie carries no `googleconsentmap`.
const FALLBACK_CATEGORY: Record<GoogleConsentType, string> = {
    ad_storage: 'targeting',
    analytics_storage: 'performance',
    ad_personalization: 'targeting',
    ad_user_data: 'targeting',
    functionality_storage: 'functionality',
    personalization_storage: 'functionality',
    security_storage: 'functionality',
};

function readCookieValue(cookieHeader: string, name: string): string | null {
    for (const part of cookieHeader.split(';')) {
        const trimmed = part.trim();
        if (trimmed.startsWith(name + '=')) return trimmed.slice(name.length + 1);
    }
    return null;
}

// The real format is a JSON string holding an array of strings; a real array is tolerated.
// Anything else, including an array with a non-string member, is not a category list this
// reader understands, and is reported as such rather than partially read.
function parseCategories(raw: unknown): string[] | null {
    let value: unknown = raw;
    if (typeof value === 'string') {
        try {
            value = JSON.parse(value);
        } catch {
            return null;
        }
    }
    if (!Array.isArray(value)) return null;
    if (!value.every((c): c is string => typeof c === 'string')) return null;
    return value;
}

// The fallback names apply only when the cookie carries no map at all. A map that is present but
// malformed, or that lacks the requested type, is a shape this reader does not understand, and
// guessing a name for it would be a way to fail open.
function categoryFor(consentType: GoogleConsentType, map: unknown): string | null {
    if (map === undefined) return FALLBACK_CATEGORY[consentType] ?? null;
    if (!map || typeof map !== 'object' || Array.isArray(map)) return null;
    const mapped = (map as Record<string, unknown>)[consentType];
    return typeof mapped === 'string' && mapped.length > 0 ? mapped : null;
}

export function readCookieScriptConsent(cookieHeader: string, consentType: GoogleConsentType): ConsentState {
    try {
        const encoded = readCookieValue(cookieHeader, COOKIE_SCRIPT_COOKIE);
        if (encoded === null) return 'unknown';

        let decoded: string;
        try {
            decoded = decodeURIComponent(encoded);
        } catch {
            return 'unknown';
        }

        const parsed: unknown = JSON.parse(decoded);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return 'unknown';
        const record = parsed as Record<string, unknown>;

        if (record.action === 'reject') return 'denied';
        if (record.action !== 'accept') return 'unknown';

        const categories = parseCategories(record.categories);
        if (categories === null) return 'unknown';

        const category = categoryFor(consentType, record.googleconsentmap);
        if (category === null) return 'unknown';

        return categories.includes(category) ? 'granted' : 'denied';
    } catch {
        return 'unknown';
    }
}
